<template>
  <div>
    <DetailTemplate
      :custom-class="'contract-detail detail-page'"
      v-if="getPermission('ot:view')"
    >
      <template v-slot:sidebar-detail>
        <v-card class="h-100" elevation="0">
          <div class="d-flex px-0 flex-wrap align-center">
            <template v-if="detail && detail.status">
              <span class="my-1">
                <v-chip
                  :color="getColor(detail.status)"
                  label
                  class="ml-2 mr-2"
                >
                  <span class="font-size-18 font-weight-600">
                    {{ getStatus(detail.status) }}
                  </span>
                </v-chip>
              </span>
            </template>
            <template v-else>
              <span class="my-1 text-muted">no status</span>
            </template>
          </div>
          <div class="px-2 py-5 text-center position-relative">
            <v-avatar size="150">
              <v-img
                :lazy-src="$defaultProfileImage"
                :src="
                  detail.engineer?.profile_logo?.file?.url ||
                  $defaultProfileImage
                "
                class="custom-grey-border"
              ></v-img>
            </v-avatar>
            <h1
              v-if="!pageLoading"
              class="form-title text-capitalize mt-4 mb-0"
            >
              {{ detail.engineer?.full_name || "No Name" }}
            </h1>
          </div>
          <div class="mt-5">
            <table width="100%">
              <tr>
                <th
                  colspan="2"
                  class="grey lighten-4 font-size-19 color-custom-blue py-1"
                >
                  Technician Information
                </th>
              </tr>
              <tr>
                <td
                  valign="middle"
                  class="py-1 font-size-18 font-weight-500"
                  colspan="2"
                >
                  <EntityAlert
                    v-if="detail?.entity?.name"
                    :name="detail.entity.name"
                  />
                </td>
              </tr>
              <tr>
                <td valign="middle" class="py-1 font-size-18 font-weight-600">
                  Technician
                </td>
                <td valign="middle" class="py-1 font-size-18 font-weight-500">
                  <template v-if="detail?.engineer">
                    <v-chip
                      small
                      class="text-white p-3 cursor-pointer mt-1 engineer-chip"
                      color="orange"
                      label
                      outlined
                      @click.prevent.stop="
                        routeToDetailLink(
                          detail.engineer.id,
                          'admin.engineer.detail'
                        )
                      "
                    >
                      {{ detail.engineer.barcode }}
                    </v-chip>
                  </template>

                  <em class="text-muted" v-else> no engineer </em>
                </td>
              </tr>
              <tr>
                <td valign="middle" class="py-1 font-size-18 font-weight-600">
                  Full Name
                </td>
                <td valign="middle" class="py-1 font-size-18 font-weight-500">
                  <label class="m-0" v-if="detail.engineer?.full_name">{{
                    detail.engineer?.full_name
                  }}</label>
                  <em class="text-muted" v-else> no name</em>
                </td>
              </tr>
              <tr>
                <td valign="middle" class="py-1 font-size-18 font-weight-600">
                  Email
                </td>
                <td valign="middle" class="py-1 font-size-18 font-weight-500">
                  <label class="m-0" v-if="detail.engineer?.primary_email">{{
                    detail.engineer?.primary_email
                  }}</label>
                  <em class="text-muted" v-else> no email</em>
                </td>
              </tr>
              <tr>
                <td valign="middle" class="py-1 font-size-18 font-weight-600">
                  Designation
                </td>

                <td valign="middle" class="py-1 font-size-18 font-weight-500">
                  <template
                    v-if="detail.engineer && detail.engineer?.designation"
                  >
                    <v-chip small outlined color="primary" label class="mr-2">
                      {{ detail.engineer?.designation.text }}
                    </v-chip>
                  </template>
                  <template v-else>
                    <em class="text-muted">no designation</em>
                  </template>
                </td>
              </tr>
              <tr>
                <td valign="middle" class="py-1 font-size-18 font-weight-600">
                  Phone
                </td>
                <td valign="middle" class="py-1 font-size-18 font-weight-500">
                  <label class="m-0" v-if="detail.engineer?.primary_phone">{{
                    detail.engineer?.primary_phone
                  }}</label>
                  <em class="text-muted" v-else> no phone no.</em>
                </td>
              </tr>
            </table>
          </div>
        </v-card>
      </template>
      <template v-slot:header-title>
        <span class="color-custom-blue font-weight-700 font-size-22 mr-2">
          {{ detail.barcode }}</span
        >
      </template>
      <template v-slot:header-action>
        <v-menu
          transition="slide-y-transition"
          bottom
          offset-y
          eager
          content-class="custom-menu-list"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              v-bind="attrs"
              v-on="on"
              v-if="
                detail.status !== 2 &&
                detail.status !== 3 &&
                getPermission('ot:edit')
              "
            >
              More... <v-icon right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <v-list>
            <template v-for="(more, index) in moreActions">
              <v-list-item
                link
                :key="index"
                @click="updateMoreAction(more.action)"
                :disabled="more.disabled"
              >
                <v-list-item-icon class="margin-auto-zero mr-3 my-2">
                  <v-icon class="icon-default-blue">{{ more.icon }}</v-icon>
                </v-list-item-icon>

                <v-list-item-title class="font-weight-500 font-size-14">
                  {{ more.title }}
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </template>
      <template v-slot:body>
        <v-container fluid v-if="pageLoaded">
          <v-tabs
            active-class="custom-tab-active"
            v-model="otTab"
            background-color="transparent"
            color="cyan"
            class="custom-tab-transparent tab-sticky"
          >
            <v-tab class="font-size-16 font-weight-600 px-8" href="#other">
              <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                <!--begin::Svg Icon-->
                <inline-svg :src="$assetURL('media/custom-svg/overview.svg')" />
                <!--end::Svg Icon-->
              </span>
              Overview
            </v-tab>
            <v-tab class="font-size-16 font-weight-600 px-8" href="#attachment">
              <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                <!--begin::Svg Icon-->
                <inline-svg
                  :src="$assetURL('media/custom-svg/attachment.svg')"
                />
                <!--end::Svg Icon-->
              </span>
              Attachment
            </v-tab>

            <v-tab class="font-size-16 font-weight-600 px-8" href="#history">
              <span class="svg-icon svg-icon-lg mr-4 svg-v-list-icon">
                <!--begin::Svg Icon-->
                <inline-svg :src="$assetURL('media/custom-svg/history.svg')" />
                <!--end::Svg Icon-->
              </span>
              History
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="otTab">
            <v-tab-item value="other">
              <v-container fluid>
                <v-card
                  flat
                  class="custom-grey-border remove-border-radius mb-4"
                >
                  <v-card-title class="headline grey lighten-4 py-1">
                    <span
                      class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                    >
                      Ot Information</span
                    >
                  </v-card-title>
                  <v-card-text class="p-6 font-size-16"> </v-card-text>
                  <v-layout class="my-4">
                    <v-flex md6 class="mr-2">
                      <table width="100%">
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-600"
                          >
                            Visit
                          </td>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            <template v-if="detail?.visit">
                              <v-chip
                                small
                                class="text-white p-3 cursor-pointer mt-1 visit-chip"
                                color="cyan"
                                label
                                outlined
                                @click.prevent.stop="
                                  routeToDetailLink(
                                    detail.visit.id,
                                    'admin.visit.detail'
                                  )
                                "
                              >
                                {{ detail.visit.barcode }}
                              </v-chip>
                            </template>

                            <em class="text-muted" v-else> no visit </em>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-600"
                          >
                            Start Date
                          </td>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            <label class="m-0" v-if="detail.start_date">{{
                              formatDate(detail.start_date)
                            }}</label>
                            <em class="text-muted" v-else> no start_date</em>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-600"
                          >
                            Start Time
                          </td>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            <template v-if="detail.start_time">
                              <v-chip
                                small
                                outlined
                                color="red"
                                label
                                class="mr-2"
                              >
                                {{ formatDateStartTime(detail.start_time) }}
                              </v-chip>
                            </template>
                            <template v-else>
                              <em class="text-muted">no started at</em>
                            </template>
                          </td>
                        </tr>

                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-600"
                          >
                            End Time
                          </td>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            <template v-if="detail && detail.end_time">
                              <v-chip
                                small
                                outlined
                                color="green"
                                label
                                class="mr-2"
                              >
                                {{ formatDateStartTime(detail.end_time) }}
                              </v-chip>
                            </template>
                            <template v-else>
                              <em class="text-muted">no ended at</em>
                            </template>
                          </td>
                        </tr>
                        <tr>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-600"
                          >
                            Reason
                          </td>
                          <td
                            valign="middle"
                            class="py-1 font-size-18 font-weight-500"
                          >
                            <label class="m-0" v-if="detail.reason">{{
                              detail.reason
                            }}</label>
                            <em class="text-muted" v-else> no reason</em>
                          </td>
                        </tr>
                      </table>
                    </v-flex>
                  </v-layout>
                </v-card>

                <v-card
                  v-if="
                    (detail.status === 2 && detail.accepted_remark) ||
                    (detail.status === 3 && detail.rejected_remark)
                  "
                  flat
                  class="custom-grey-border remove-border-radius mb-4"
                >
                  <v-card-title class="headline grey lighten-4 py-1">
                    <span
                      class="font-weight-700 custom-headline color-custom-blue font-size-16 text-uppercase"
                    >
                      Remark
                    </span>
                  </v-card-title>

                  <v-card-text class="p-6 font-size-16">
                    <read-more
                      class="custom-read-more font-weight-600 font-size-18 custom-gray-color text-capitalize"
                      more-str="read more"
                      less-str="read less"
                      :max-chars="200"
                      :text="
                        detail.status === 2
                          ? detail.accepted_remark || 'No remark'
                          : detail.status === 3
                          ? detail.rejected_remark || 'No remark'
                          : 'No remark'
                      "
                    />
                  </v-card-text>
                </v-card>
              </v-container>
            </v-tab-item>
            <v-tab-item :value="'attachment'">
              <AdditionalDocuments
                :type-id="detail.id"
                class="mx-4"
                type-text="Ot Documents"
                :type="20"
                :extensive="false"
              ></AdditionalDocuments>
            </v-tab-item>

            <v-tab-item :value="'history'">
              <InternalHistoryDetail
                type="ot"
                :type_id="detail.id"
              ></InternalHistoryDetail>
            </v-tab-item>
          </v-tabs-items>
        </v-container>
      </template>
    </DetailTemplate>
    <Dialog :commonDialog="confirm_dialog" :dialog-width="dialogWidth">
      <template v-slot:title> Confirmation </template>
      <template v-slot:body>
        <v-form ref="statusForm" v-model.trim="formValid" lazy-validation>
          <v-container class="px-10" fluid>
            <p
              class="font-weight-600 font-size-19"
              v-if="
                confirm_type === 'mark_as_accepted' ||
                confirm_type === 'mark_as_rejected'
              "
            >
              Are you sure, you want to
            </p>
            <v-layout
              class="my-4"
              v-if="
                confirm_type == 'mark_as_rejected' ||
                confirm_type == 'mark_as_accepted'
              "
            >
              <v-flex>
                <label
                  class="font-weight-500 font-size-16"
                  :class="{
                    required: confirm_type == 'mark_as_rejected',
                  }"
                >
                  Remark
                  <span
                    v-if="confirm_type == 'mark_as_rejected'"
                    class="red--text"
                    >*</span
                  >
                </label>
                <TextAreaField
                  :rules="getRemarkRules()"
                  auto-grow
                  dense
                  filled
                  color="cyan"
                  v-model="actionData.remark"
                  :placeholder="
                    confirm_type == 'mark_as_accepted'
                      ? 'Enter your remark (optional)'
                      : 'Enter your remark'
                  "
                  solo
                  flat
                  row-height="25"
                  counter="200"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          :disabled="confirm_loading"
          v-on:click="confirm_dialog = false"
          class="mx-2 custom-grey-border custom-bold-button"
        >
          NO! CLOSE
        </v-btn>
        <v-btn
          depressed
          v-on:click="confirm_submit()"
          :disabled="!formValid || confirm_loading"
          :loading="confirm_loading"
          class="mx-2 custom-bold-button white--text"
          color="cyan"
        >
          YES! CONTINUE
        </v-btn>
      </template>
    </Dialog>
  </div>
</template>
<script>
import moment from "moment-timezone";
moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import CommonMixin from "@/core/plugins/common-mixin";
import DetailTemplate from "@/view/pages/partials/Detail-Template.vue";
import { GET, PATCH } from "@/core/services/store/request.module";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import ValidationMixin from "@/core/plugins/validation-mixin";
import InternalHistoryDetail from "@/view/pages/partials/Detail/Internal-History-Detail.vue";
import AdditionalDocuments from "@/view/pages/engineer/Additional-Documents";
import EntityAlert from "@/view/components/ShowEntityName.vue";
import Dialog from "@/view/pages/partials/Dialog";

export default {
  mixins: [CommonMixin, ValidationMixin],
  data() {
    return {
      otId: 0,
      pageLoading: true,
      pageLoaded: false,
      statusLoading: false,
      confirm_dialog: false,
      otTab: "other",
      moreActions: new Array(),
      detail: {},
      dbLineItems: [],
      dbEquipments: [],
      confirm_type: null,
      confirm_loading: false,
      actionData: {
        rejected_remark: null,
        accepted_remark: null,
        status: null,
      },
    };
  },

  methods: {
    formatDateStartTime(row) {
      return moment(row).format("hh:mm A");
    },
    getStatus(status) {
      if (status == 1) {
        return "Pending";
      } else if (status == 2) {
        return "Approved";
      } else if (status == 3) {
        return "Rejected";
      }
      return "Unknown";
    },
    getColor(status) {
      if (status == 1) {
        return "orange white--text";
      } else if (status == 2) {
        return "green white--text";
      } else if (status == 3) {
        return "red white--text";
      }
      return "grey white--text";
    },
    routeToDetailLink(id, type) {
      this.$router.push({
        name: type,
        params: { id },
      });
    },

    updateMoreAction(type) {
      switch (type) {
        case "mark_as_accepted":
          this.confirm_dialog = true;
          this.confirm_type = "mark_as_accepted";
          break;

        case "mark_as_rejected":
          this.confirm_dialog = true;
          this.confirm_type = "mark_as_rejected";
          break;
      }
    },
    confirm_submit() {
      const _this = this;
      const validateStatus = _this.$refs.statusForm.validate();

      const formErrors = _this.validateForm(_this.$refs.statusForm);
      if (formErrors.length) {
        for (let i = 0; i < formErrors.length; i++) {
          ErrorEventBus.$emit("update:error", InitializeError(formErrors[i]));
        }
        return false;
      }
      if (!validateStatus) {
        return false;
      }
      _this.confirm_loading = true;
      if (this.confirm_type === "mark_as_accepted") {
        this.updateStatus(2);
      }

      if (this.confirm_type === "mark_as_rejected") {
        this.updateStatus(3);
      }
    },
    get_confirm_title() {
      if (this.confirm_type == "mark_as_rejected") {
        return "Mark as Rejected";
      } else if (this.confirm_type == "mark_as_accepted") {
        return "Mark as Accepted";
      }
    },
    getRemarkRules() {
      if (this.confirm_type === "mark_as_rejected") {
        return [this.validateRules.required(this.actionData.remark, "Remark")];
      }
      return [];
    },
    updateStatus(status) {
      if (this.statusLoading) {
        return false;
      }
      this.statusLoading = true;

      this.actionData.status = status;
      if (status === 3) {
        this.actionData.rejected_remark = this.actionData.remark;
      }
      if (status === 2) {
        this.actionData.accepted_remark = this.actionData.remark;
      }
      this.$store
        .dispatch(PATCH, {
          url: "ot/" + this.otId,
          data: this.actionData,
        })
        .then(() => {
          this.getOt();
          this.confirm_loading = false;
          this.confirm_dialog = false;
          this.confirm_type = null;
          this.actionData.remark = null;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.statusLoading = false;
          this.confirm_loading = false;
        });
    },

    getOt() {
      this.$store
        .dispatch(GET, { url: "ot/" + this.otId })
        .then(({ data }) => {
          this.detail = data;
          this.moreActions = data.more_actions;
          this.db_approval = data.db_approval;
          this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Ot", route: "ot" },
            { title: "Detail" },
            { title: data.barcode },
          ]);
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.pageLoaded = true;
        });
    },
  },
  created() {
    this.otId = this.lodash.toSafeInteger(this.$route.params.id);
  },
  computed: {
    dialogWidth() {
      // return 400;
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 40);
    },
  },
  mounted() {
    this.getOt();
  },
  components: {
    DetailTemplate,
    InternalHistoryDetail,
    AdditionalDocuments,
    EntityAlert,
    Dialog,
  },
};
</script>
